import { useCallback, useEffect, useRef, useState } from 'react';

import { getJWTToken } from '../../utils/auth';
import { authHeaders } from '../../core/impersonation';
import type { ScoreUnit } from '../ProgramEvaluation/types';

const apiUrl = import.meta.env.REACT_APP_BAMBOO_API_URL ?? '';
const POLL_INTERVAL_MS = 4000;
const MAX_CONSECUTIVE_READ_FAILURES = 5;

export type PeStatusKind = 'none' | 'generating' | 'generated';

export type PeStatus = {
  status: PeStatusKind;
  schoolYear: string | null;
  generatedAt: string | null;
  stale: boolean;
  jobId: string | null;
  unitMode: ScoreUnit | null;
  cannotGenerate: boolean;
};

export type PeStatusSource = () => Promise<PeStatus>;

type StatusBody = Partial<{
  status: PeStatusKind;
  schoolYear: string;
  generatedAt: string;
  stale: boolean;
  jobId: string;
  unitMode: ScoreUnit;
  cannotGenerate: boolean;
}>;

export async function fetchProgramEvaluationStatus(): Promise<PeStatus> {
  const token = await getJWTToken();
  const response = await fetch(`${apiUrl}/program-evaluation/status`, {
    headers: authHeaders(token),
  });
  if (!response.ok) {
    throw new Error(`program-evaluation status failed: ${response.status}`);
  }
  const body = ((await response.json()) ?? {}) as StatusBody;
  return {
    status: body.status ?? 'none',
    schoolYear: body.schoolYear ?? null,
    generatedAt: body.generatedAt ?? null,
    stale: body.stale === true,
    jobId: body.jobId ?? null,
    unitMode: body.unitMode ?? null,
    cannotGenerate: body.cannotGenerate === true,
  };
}

export function useProgramEvaluationStatus(
  source: PeStatusSource,
  enabled: boolean,
): { status: PeStatus | null; failed: boolean; givenUp: boolean; refetch: () => void } {
  const [status, setStatus] = useState<PeStatus | null>(null);
  const [failed, setFailed] = useState(false);
  const [givenUp, setGivenUp] = useState(false);
  const [readCount, setReadCount] = useState(0);
  const sourceRef = useRef(source);

  const refetch = useCallback(() => {
    setGivenUp(false);
    setReadCount((count) => count + 1);
  }, []);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let consecutiveFailures = 0;
    const read = () => {
      sourceRef
        .current()
        .then((next) => {
          if (!active) return;
          consecutiveFailures = 0;
          setStatus(next);
          setFailed(false);
          if (next.status === 'generating') {
            timer = setTimeout(read, POLL_INTERVAL_MS);
          }
        })
        .catch((err) => {
          console.warn('program-evaluation status unavailable', err);
          if (!active) return;
          consecutiveFailures += 1;
          setFailed(true);
          if (consecutiveFailures < MAX_CONSECUTIVE_READ_FAILURES) {
            timer = setTimeout(read, POLL_INTERVAL_MS);
          } else {
            setGivenUp(true);
          }
        });
    };
    read();
    return () => {
      active = false;
      if (timer !== null) clearTimeout(timer);
    };
  }, [enabled, readCount]);

  return { status, failed, givenUp, refetch };
}
